import { useEffect, useState, useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';

import { useTexture, useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader';

import useMode from '../../stores/mode';

function Marker({ marker }) {
    return marker?.type === 'video' ? (
        <VideoMarker poster={marker?.video?.poster} portrait={marker?.video?.portrait}></VideoMarker>
    ) : marker?.type === 'chat' ? (
        <ChatMarker></ChatMarker>
    ) : marker?.type === 'link' ? (
        <LinkMarker poster={marker?.link?.poster} url={marker?.link?.url}></LinkMarker>
    ) : (
        <></>
    );
}

function VideoMarker({ poster, portrait = false }) {
    const mode = useMode();

    const screen = useRef();
    const time = useRef(0);

    useFrame((state, delta) => {
        time.current += delta;

        if (!screen.current) {
            return;
        }

        screen.current.rotation.y = 0.5 * Math.sin(time.current / 2);
    });

    return !poster ? (
        <></>
    ) : (
        <group
            scale={[1, 1, 1]}
            onClick={() => {
                console.log('Video.onClick()');

                mode.set('item');
            }}
        >
            {/* BASE */}
            <mesh position={[0, 0.125, 0]} scale={[1, 1, 1]} receiveShadow castShadow>
                <cylinderGeometry args={[0.8, 1.0, 0.25, 32]} />
                <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
            </mesh>

            <group
                position={portrait ? [0, 1, 0] : [0, 1.0, 0]}
                scale={portrait ? [0.6, 0.6, 0.6] : [0.8, 0.8, 0.8]}
                ref={screen}
            >
                <group rotation={[-0.8, 0, 0]}>
                    {/* PLAY ICON */}
                    <mesh
                        position={portrait ? [0, 0.2, 0.25] : [-1.2, 0.7, 0.25]}
                        rotation={[0, Math.PI / 2, Math.PI / 2]}
                        scale={[0.5, 0.5, 0.5]}
                        castShadow
                    >
                        <cylinderGeometry args={[1, 1, 0.2, 3]} />
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    {/* PLAY ICON */}
                    <mesh
                        position={portrait ? [0, 0.2, -0.25] : [1.2, 0.7, -0.25]}
                        rotation={[0, -Math.PI / 2, Math.PI / 2]}
                        scale={[0.5, 0.5, 0.5]}
                        castShadow
                    >
                        <cylinderGeometry args={[1, 1, 0.2, 3]} />
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    {/* SCREEN */}
                    <mesh scale={[2, 2, 2]} position={[0, 1, 0.205]} rotation={[0, 0, 0]} receiveShadow>
                        <planeBufferGeometry args={portrait ? [1, 1.75] : [1.75, 1]} />

                        <ScreenMaterial poster={poster} />
                    </mesh>

                    {/* SCREEN */}
                    <mesh scale={[2, 2, 2]} position={[0, 1, -0.205]} rotation={[0, Math.PI, 0]} receiveShadow>
                        <planeBufferGeometry args={portrait ? [1, 1.75] : [1.75, 1]} />

                        <ScreenMaterial poster={poster} />
                    </mesh>

                    {/* CASE */}
                    <mesh scale={[2, 2, 2]} position={[0, 1, 0]} rotation={[0, 0, 0]} castShadow>
                        <boxBufferGeometry args={portrait ? [1, 1.75, 0.2] : [1.75, 1, 0.2]} />

                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>
                    <mesh scale={[2, 2, 2]} position={[0, 1, 0]} rotation={[0, 0, 0]} castShadow>
                        <boxBufferGeometry args={portrait ? [1.05, 1.8, 0.1] : [1.8, 1.05, 0.1]} />

                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>
                </group>
            </group>

            {/* FLOOR */}
            <mesh position={[0, 0, 0]} scale={[1, 1, 1]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeBufferGeometry args={[10, 10]} />

                <shadowMaterial color="#000000" opacity={0.05} />
            </mesh>
        </group>
    );
}

function LinkMarker({ poster, url }) {
    const mode = useMode();

    const screen = useRef();
    const time = useRef(0);

    const svg = useMemo(() => {
        const _svg = new SVGLoader().parse(
            `<svg xmlns="http://www.w3.org/2000/svg" id="svg5" width="64" height="64" version="1.1" viewBox="0 0 16.933 16.933"><g id="layer1"><path id="path904" fill="#000" stroke-width="1" d="m7.593 16.394-.204-.024c-.875-.1-1.778-.483-2.502-1.062-.361-.288-.532-.482-2.371-2.7C.896 10.654.75 10.474.676 10.325a1.323 1.323 0 0 1 .897-1.896 1.54 1.54 0 0 1 .69.04c.305.103.3.1 1.145.939.569.565.78.765.81.767a.261.261 0 0 0 .151-.03c.1-.05.093.246.093-4.29 0-4.559-.007-4.26.097-4.51C4.999.28 6.507.25 6.97 1.302c.106.24.098.046.108 2.615l.008 2.335.094-.115c.744-.91 2.192-.482 2.34.692l.02.162.031-.047c.197-.302.613-.533 1.006-.558.714-.046 1.385.555 1.391 1.246l.001.098.06-.073c.685-.843 1.945-.658 2.309.338.085.233.085.233.079 2.433-.007 2.28-.002 2.177-.126 2.682a4.35 4.35 0 0 1-3.73 3.271c-.187.022-2.793.034-2.968.014z"/></g></svg>`
        );

        return SVGLoader.createShapes(_svg?.paths?.[0]);
    });

    useFrame((state, delta) => {
        time.current += delta;

        if (!screen.current) {
            return;
        }

        screen.current.rotation.y = 0.5 * Math.sin(time.current / 2);
    });

    return !poster ? (
        <></>
    ) : (
        <group
            scale={[1, 1, 1]}
            onClick={() => {
                console.log('LinkMarker.onClick()', url);
                if (url) {
                    window.location.href = url;
                }
            }}
        >
            {/* BASE */}
            <mesh position={[0, 0.125, 0]} scale={[1, 1, 1]} receiveShadow castShadow>
                <cylinderGeometry args={[0.8, 1.0, 0.25, 32]} />
                <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
            </mesh>

            <group position={[0, 1.0, 0]} scale={[0.8, 0.8, 0.8]} ref={screen}>
                <group rotation={[-0.8, 0, 0]}>
                    {/* CURSOR ICON */}
                    <mesh
                        scale={[0.08, 0.08, 0.06]}
                        position={[-0.2, 0.9, 0.3]}
                        rotation={[0, Math.PI, Math.PI]}
                        receiveShadow
                    >
                        <extrudeGeometry args={[svg, { depth: 6, bevelSegments: 0, bevelSize: 0, bevelOffset: 0 }]} />
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    {/* SCREEN */}
                    <mesh scale={[2, 2, 2]} position={[0, 1, 0.205]} rotation={[0, 0, 0]} receiveShadow>
                        <planeBufferGeometry args={[1.5, 0.9]} />

                        <ScreenMaterial poster={poster} />
                    </mesh>
                    <mesh scale={[2, 2, 2]} position={[0, 1, -0.205]} rotation={[0, Math.PI, 0]} receiveShadow>
                        <planeBufferGeometry args={[1.5, 0.9]} />

                        <ScreenMaterial poster={poster} />
                    </mesh>

                    {/* CASE */}
                    <mesh scale={[2, 2, 2]} position={[0, 1, 0]} rotation={[0, 0, 0]} castShadow>
                        <boxBufferGeometry args={[1.5, 0.9, 0.2]} />

                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>
                    <mesh scale={[2, 2, 2]} position={[0, 1.1, 0]} rotation={[0, 0, 0]} castShadow>
                        <boxBufferGeometry args={[1.55, 1.05, 0.1]} />

                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>

                    {/* BUTTONS */}
                    <mesh
                        position={[-1.4, 2.05, 0.0]}
                        rotation={[0, Math.PI / 2, Math.PI / 2]}
                        scale={[0.1, 0.1, 0.1]}
                        castShadow
                    >
                        <cylinderGeometry args={[0.8, 0.8, 3.2, 24]} />
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>
                    <mesh
                        position={[-1.15, 2.05, 0.0]}
                        rotation={[0, Math.PI / 2, Math.PI / 2]}
                        scale={[0.1, 0.1, 0.1]}
                        castShadow
                    >
                        <cylinderGeometry args={[0.8, 0.8, 3.2, 24]} />
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>
                </group>
            </group>

            {/* FLOOR */}
            <mesh position={[0, 0, 0]} scale={[1, 1, 1]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeBufferGeometry args={[10, 10]} />

                <shadowMaterial color="#000000" opacity={0.05} />
            </mesh>
        </group>
    );
}

function ChatMarker() {
    const mode = useMode();

    const { nodes, materials } = useGLTF('./models/marker--chat.gltf');

    const bubble1 = useRef();
    const bubble2 = useRef();
    const time = useRef(0);

    useFrame((state, delta) => {
        time.current += delta;

        if (!bubble1.current || !bubble2.current) {
            return;
        }

        bubble1.current.position.y = 0.5 * Math.sin(time.current * 1.7);
        bubble2.current.position.y = 0.5 * Math.sin((time.current + 1000) * 1.7);
    });

    console.log('nodes', nodes);

    return (
        <group
            scale={[1, 1, 1]}
            onClick={() => {
                console.log('ChatMarker.onClick()');

                mode.set('item');
            }}
        >
            {/* BASE */}
            <mesh position={[0, 0.125, 0]} scale={[1, 1, 1]} receiveShadow castShadow>
                <cylinderGeometry args={[0.8, 1.0, 0.25, 32]} />
                <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
            </mesh>

            {/* BUBBLES */}
            <group scale={[0.3, 0.3, 0.3]} position={[0, 0.5, 0]} rotation={[-0.8, 0, 0]}>
                {/* BUBBLE 1 */}
                <group scale={[1, 1, 1]} ref={bubble1}>
                    <mesh geometry={nodes?.['bubble-1']?.geometry} castShadow>
                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>

                    <mesh geometry={nodes?.['text-1']?.geometry}>
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    <mesh geometry={nodes?.['image-1']?.geometry}>
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    <mesh position={[4.4, 6.65, -0.675]} scale={[1.55, 1.55, 1.55]} rotation={[0, 0, 0]}>
                        <circleGeometry args={[1, 32]} />
                        <PhotoMaterial poster={'./images/Andrea-Christ.jpg'}></PhotoMaterial>
                    </mesh>
                </group>

                {/* BUBBLE 2 */}
                <group scale={[1, 1, 1]} ref={bubble2}>
                    <mesh geometry={nodes?.['bubble-2']?.geometry} castShadow>
                        <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
                    </mesh>

                    <mesh geometry={nodes?.['text-2']?.geometry}>
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    <mesh geometry={nodes?.['image-2']?.geometry}>
                        <PlasticMaterial color={'#eeeeee'}></PlasticMaterial>
                    </mesh>

                    <mesh position={[-3.95, 4.05, 1.15]} scale={[1.55, 1.55, 1.55]} rotation={[0, 0, 0]}>
                        <circleGeometry args={[1, 32]} />
                        <PhotoMaterial poster={'./images/Gesa-Giesing.jpg'}></PhotoMaterial>
                    </mesh>
                </group>
            </group>

            <mesh position={[0, 0, 0]} scale={[1, 1, 1]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
                <planeBufferGeometry args={[20, 20]} />

                <shadowMaterial color="#000000" opacity={0.05} />
            </mesh>
        </group>
    );
}

function DummyMarker({ color }) {
    const torus = useRef();
    const sphere1 = useRef();
    const diamond = useRef();
    const time = useRef(0);

    useFrame((state, delta) => {
        // foo
        time.current += delta;

        if (!torus.current || !diamond.current || !sphere1.current) {
            return;
        }

        torus.current.rotation.y += 0.01;
        diamond.current.rotation.y -= 0.01;
        sphere1.current.position.y = 2 + Math.sin(time.current) * 1;
    });

    return (
        <>
            <mesh position={[0, 0.1, 0]} scale={[1, 0.2, 1]}>
                <boxBufferGeometry />
                <PlasticMaterial color={'#bee1de'}></PlasticMaterial>
            </mesh>
            <mesh position={[0, 2, 0]} scale={[0.5, 0.5, 0.5]} ref={torus}>
                <torusKnotGeometry args={[1, 0.4, 128, 32]} />
                <PlasticMaterial color={color}></PlasticMaterial>
            </mesh>
            <mesh position={[1, 2, 1]} scale={[0.6, 0.8, 0.6]} ref={diamond}>
                <octahedronGeometry args={[1, 0]} />
                <PlasticMaterial color={color}></PlasticMaterial>
            </mesh>
            <mesh position={[-1, 1, -1]} scale={[0.4, 0.4, 0.4]} ref={sphere1}>
                <sphereBufferGeometry args={[1, 64, 64]} />
                <PlasticMaterial color={color}></PlasticMaterial>
            </mesh>
            <mesh position={[0, -0.05, 0]} scale={[1, 1, 1]} rotation={[-Math.PI / 2, 0, 0]}>
                <planeBufferGeometry args={[20, 20]} />

                <shadowMaterial color="#000000" opacity={0.15} />
            </mesh>
        </>
    );
}

function PlasticMaterial({ color, props }) {
    return <meshStandardMaterial color={color} envMapIntensity={0.25} />;
}

function ScreenMaterial({ poster }) {
    const map = useTexture(poster);

    return (
        <meshStandardMaterial
            map={map}
            color={'#ffffff'}
            roughness={0.5}
            metalness={0.5}
            envMapIntensity={0.25}
        ></meshStandardMaterial>
    );
}

function PhotoMaterial({ poster }) {
    const map = useTexture(poster);

    return (
        <meshStandardMaterial
            map={map}
            color={'#ffffff'}
            roughness={0.5}
            metalness={0.5}
            envMapIntensity={0.25}
            side={THREE.DoubleSide}
        ></meshStandardMaterial>
    );
}

export default Marker;
